import type { MenuItemType } from "@/features/list/api/updateMenuItemOrder";
import { useUpdateMenuItemOrder } from "@/features/list/api/updateMenuItemOrder";
import { useWorkflowData } from "@/features/list/hooks/useWorkflowData";
import useFolderStore from "@/store/ui/folderStore";
import { atom, useAtom } from "jotai";
import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const useMenuData = () => {
  const { unassignedWorkflows, folders } = useWorkflowData();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTermAtom = useMemo(
    () => atom<string>(searchParams.get("searchTerm") || ""),
    [searchParams]
  );
  const [searchTerm, setSearchTerm] = useAtom(searchTermAtom);
  const updateMenuItemOrderMutation = useUpdateMenuItemOrder({});
  const { openFolder, closeAllFolders } = useFolderStore();
  // 前回の検索語を記録するためのref
  const prevSearchTermRef = useRef("");

  // フィルタリングされたデータをuseMemoで最適化
  const filteredData = useMemo(() => {
    const searchTermLower = searchTerm.toLowerCase().trim();
    const prevSearchTermLower = prevSearchTermRef.current.toLowerCase().trim();

    // 検索語が空になった場合（以前は空でなかった）、すべてのフォルダを閉じる
    if (!searchTermLower && prevSearchTermLower) {
      closeAllFolders();
    }

    // 現在の検索語を保存
    prevSearchTermRef.current = searchTermLower;

    // 検索語が空の場合は元のデータをそのまま返す
    if (!searchTermLower) {
      return {
        folders,
        unassignedWorkflows,
      };
    }

    // ルール検索ロジック：ワークフロー名が検索語に一致するワークフローのみを表示
    const workflowFilteredFolders = folders
      .map((folder) => ({
        ...folder,
        workflows: folder.workflows.filter((workflow) =>
          workflow.workflow_name.toLowerCase().includes(searchTermLower)
        ),
      }))
      .filter((folder) => folder.workflows.length > 0); // ワークフローがないフォルダは表示しない

    // 検索語がある場合、一致するフォルダを自動的に開く
    workflowFilteredFolders.forEach((folder) => {
      openFolder(folder.id);
    });

    return {
      folders: workflowFilteredFolders,
      unassignedWorkflows: unassignedWorkflows.filter((workflow) =>
        workflow.workflow_name.toLowerCase().includes(searchTermLower)
      ),
    };
  }, [folders, unassignedWorkflows, searchTerm, openFolder, closeAllFolders]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);

    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (newSearchTerm) {
          params.set("searchTerm", newSearchTerm);
        } else {
          params.delete("searchTerm");
        }
        return params;
      },
      { replace: true }
    );

    localStorage.setItem("searchTerm", newSearchTerm);
  };

  const [changing, setChanging] = useState(false);
  const handleUpdateMenuOrder = async ({
    itemId,
    itemType,
    order,
    parentFolderId,
  }: {
    itemId: string;
    itemType: MenuItemType;
    order: number;
    parentFolderId?: string | null;
  }) => {
    try {
      setChanging(true);
      await updateMenuItemOrderMutation.mutateAsync({
        item_id: itemId,
        item_type: itemType,
        order,
        parent_folder_id: parentFolderId,
      });
    } catch (error) {
      throw error;
    } finally {
      setChanging(false);
    }
  };

  return {
    searchTerm,
    setSearchTerm,
    handleSearchChange,
    filteredFolders: filteredData.folders,
    filteredUnassignedWorkflows: filteredData.unassignedWorkflows,
    folders,
    isLoading: false,
    handleUpdateMenuOrder,
    changing,
  };
};
